import { createTheme, alpha, lighten, getContrastRatio } from '@mui/material/styles';

// custom colors
let buttonColor = '#4770FF';

// mui theme
export const muiTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      light: alpha('#FF4770', 0.2),
      soft: alpha('#FF4770', 0.5),
      main: '#FF4770',
      faint: lighten('#FF4770', .9),
      contrastText: "#fefefe"
    },
    secondary: {
      light: alpha('#8D47FF', 0.15),
      soft: alpha('#8D47FF', 0.7),
      main: '#8D47FF',
      faint: lighten('#8D47FF', .93),
    },
    tertiary: {
      light: alpha(buttonColor, 0.5),
      main: buttonColor,
      dark: "#1A4BFF",
      contrastText: "#FFF"
    },
    background: {
      default: '#F8F8F8',
      paper: '#FFF',
      soft: '#f3f3f3',
      light: '#f7fafb',
      faint: "#f7f7f7"
    },
    bg: {
      soft: '#f4f4f4',
      light: '#f7fafb',
      main: lighten('#EFEFEF', .3),
      dark: '#EFEFEF',
    },
    text: {
      primary: '#1E202B',
      disabled: '#e1e1e2',
      hint: '#9B9B9F',
    },
    basic: {
      light: lighten('#5d5d5d', .9),
      main: '#5d5d5d',
      disabled: '#e1e1e2',
      hint: '#9B9B9F',
      contrastText: "#FFF"
    },
    icon: {
      main: '#8e8e8e',
      contrastText: "#FFF"
    },
    error: {
      light: alpha('#F13A3A', 0.15),
      main: '#F13A3A',
      lighter: lighten('#F13A3A', .2),
      faint: lighten('#F13A3A', .9),
    },
    success: {
      light: alpha('#339900', 0.15),
      soft: alpha('#339900', 0.5),
      main: '#339900',
      faint: lighten('#339900', .9),
    },
    warning: {
      light: alpha('#FFA900', 0.15),
      soft: alpha('#FFA900', 0.5),
      main: '#FFA900',
    },
    info: {
      main: '#0086B3',
      light: lighten('#0086B3', .8),
      faint: lighten('#0086B3', .9),
    },
    inActive: {
      main: '#9B9B9F',
    },
    white: {
      main: '#FFF',
    }
  },
  typography: {
    htmlFontSize: 10,
    fontFamily: "'Space Grotesk', 'Roboto', 'Helvetica', 'Arial', sans-serif",
  },
  components: {
    MuiTypography: {
      styleOverrides: {
        h1: {
          fontSize: '2rem',
          fontWeight: "normal"
        },
        h6: {
          fontSize: '1.6rem',
        },
        h5: {
          fontSize: '1.8rem',
        },
        body1: {
          fontSize: '1.4rem',
        }
      }
    },
    MuiCardHeader: {
      styleOverrides: {
        title: {
          htmlFontSize: '1.6rem',
        }
      }
    },
    TextField: {
      defaultProps: {
        size: 'small',
      }
    }
  },
  breakpoints: {
    values: {
      xs: 0,
      smp: 450,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  }
});

const size = {
  xs: '0',
  smp: '450px',
  sm: '600px',
  md: '960px',
  lg: '1280px',
  xl: '1920px',
}

// media queries
// @media ${props => props.theme.breaks.down('md')} { 
//     
//   }

export const theme = {
  colors: {
    body: '#F8F8F8',
    lightBg: '#f7fafb',
    darkBg: '#EFEFEF', 
    softBg: "#f4f4f4",
    white: '#FFF',
    dark: 'rgb(50, 48, 49)',
    text: '#1E202B',
    mildText: '#757575',
    lightText: '#9B9B9F',
    veryLightText: '#C4C4C4',
    disabledText: "#e1e1e2",
    text: {
      primary: '#1E202B',
      muted: '#2a2d3c',
      disabled: '#e1e1e2',
      hint: '#9B9B9F',
    },
    background: {
      default: '#F8F8F8',
      paper: '#FFF',
    },
    border: '#DEDEDF',
    borderDark: "#cbcbcb",
    success: "#4BB543", //  #a6b5d8
    successBg: lighten('#339900', .85),
    ribbon: "#E7EBFA",
    info: "#0086B3",
    errorBg: "#fde7e7",
    error: '#F13A3A',
    primaryFaint: '#f9e6ea',
    primaryLighten: '#ff6687',
    primaryMid: "#FF99AF",
    primary: "#FF4770",
    primaryDarken: '#ff1a4b',
    primaryContrast: '#FFF',
    secondaryFaint: '#ede6f9',
    secondaryLighten: '#a166ff',
    secondaryMid: "#c099ff",
    secondary: "#8D47FF",
    secondaryDarken: '#711aff',
    secondaryContrast: '#FFF',
    tertiary: buttonColor,
    tertiarySoften: alpha(buttonColor, 0.8),
    warning: "#FFA900",
  },
  palette: {...muiTheme.palette},
  shadows: {...muiTheme.shadows},
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  breaks: {
    up: bp => `(min-width: ${size[bp]})`,
    down: bp => `(max-width: ${size[bp]})`,
    bewteen: (bp1, bp2) => `(min-width: ${size[bp1]}) and (max-width: ${size[bp2]})`,
    only: bp => `(width: ${size[bp]})`
  },
  mixins: {
    toolbar: {
      minHeight: '7rem'
    }
  },
  typography: {
    fontFamily: "'Space Grotesk', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    htmlFontSize: 10,
    h1: {
      fontSize: '2.8rem',
      fontFamily: "'Space Grotesk', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    },
    h6: {
      fontSize: '1.6rem',
      fontFamily: "'Space Grotesk', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    },
    h5: {
      fontSize: '1.8rem',
      fontFamily: "'Space Grotesk', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    },
    body1: {
      fontSize: '1.4rem',
      fontFamily: "'Space Grotesk', 'Roboto', 'Helvetica', 'Arial', sans-serif",
    }
  },
  maxWidth: '1280px',
  maxWidthMd: '960px'
}